import { Avatar, DecimalInput, SelectField, parseEurInput } from "@gladis/ui";
import { formatPercent } from "@gladis/shared";
import { useT } from "@gladis/ui/i18n";
import { useState, type ReactNode } from "react";

export interface EditableSplit {
  memberId: string;
  memberDisplayName: string;
  percentage: number;
  /** A share belonging to someone who is no longer a member. Read-only, and it still counts. */
  isFrozen: boolean;
}

/**
 * Who a cost belongs to, as percentages that must total exactly 100%.
 *
 * A SPLIT RECORDS RESPONSIBILITY AND NOTHING ELSE. It says whose cost this is; it says nothing about where the
 * money came from, which is the card (US4). A card owned by one member can pay an expense that member holds 0%
 * of, and the difference between those two facts is the settle-up.
 *
 * ==========================================================================================
 * FROZEN SHARES ARE READ-ONLY AND THEY STILL COUNT
 * ==========================================================================================
 *
 * A share belonging to someone who has left the household keeps its percentage (FR-033) and keeps the name it
 * was recorded under. The member editing this form cannot change it and cannot remove it — but it counts
 * towards the 100% they are trying to reach, so the editable capacity of a split holding a frozen 40% share is
 * 60%.
 *
 * IT IS ALSO SUBMITTED. The obvious form offers only current members and sends only their shares, which
 * deletes the frozen one and leaves the split at 60%; the server refuses that, and this component is the
 * reason it never has to — every share, frozen included, goes back in the payload. See research.md R10.
 */
export function SplitEditor({
  value,
  members,
  onChange,
}: {
  value: readonly EditableSplit[];
  /**
   * Current active members, from identity. Only these may be ADDED.
   *
   * `avatarUrl` is optional rather than required so a caller that has not wired faces yet still
   * compiles and simply shows initials — the degradation this component should have.
   */
  members: readonly { userId: string; displayName: string; avatarUrl?: string | null }[];
  onChange: (next: EditableSplit[]) => void;
}): ReactNode {
  const t = useT();
  /*
   * Summed in integer HUNDREDTHS OF A PERCENT, not in floats.
   *
   * `33.33 + 33.33 + 33.34` is `100.00000000000001` in binary64, so a float sum needs an epsilon — and an
   * epsilon is a threshold somebody eventually gets wrong. The domain holds these as basis points for exactly
   * this reason; the form does the same arithmetic so that what it enables and what the server accepts cannot
   * disagree.
   */
  const totalHundredths = value.reduce((sum, split) => sum + Math.round(split.percentage * 100), 0);
  const isExactlyHundred = totalHundredths === 10_000;

  const available = members.filter(
    (member) => !value.some((split) => split.memberId === member.userId),
  );

  /*
   * ==========================================================================================
   * THE RAW TEXT PER ROW, AND WHY THE FIELD NEEDS ONE AT ALL
   * ==========================================================================================
   *
   * This was `<input type="number">`, which is a trap for a decimal for the reasons `MoneyInput`
   * lists — and one of them bites hardest HERE: the decimal separator of `type="number"` follows the
   * BROWSER's locale rather than the document's, so a Dutch household typing "33,33" into a browser
   * set to en-US gets an empty value with no explanation. A share silently reading zero is a split
   * that will not submit and does not say why. The scroll wheel over a focused field is the other:
   * it changes a share by a whole percent and nothing announces it.
   *
   * `DecimalInput` accepts BOTH separators and rejects a keystroke rather than sanitising it. It is
   * controlled by a STRING, because a half-typed amount passes through states a number cannot hold —
   * "", "33." and "33," are all on the way to "33,33" — and re-deriving the text from the number on
   * every keystroke erases the separator the member just pressed.
   *
   * So the number stays the source of truth and the text is a DRAFT beside it, held only while the
   * two disagree. `percentage` is untouched: every total on this page, `splitsAreSubmittable` and the
   * server all keep reading integer hundredths of the number, which is the arithmetic this component
   * exists to get right and the last thing that should change to fix a control.
   */
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  /** What the field shows: the member's own keystrokes, or the committed share in their format. */
  function textFor(split: EditableSplit): string {
    return drafts[split.memberId] ?? formatShareInput(split.percentage);
  }

  function onShareTyped(memberId: string, next: string): void {
    setDrafts((current) => ({ ...current, [memberId]: next }));

    /*
     * COMMITTED ONLY WHEN THE TEXT PARSES. "33," parses to nothing, so the number keeps its previous
     * value and the total below stays truthful about what is actually entered — rather than dropping
     * to zero for the one keystroke between "33" and "33,3".
     *
     * `parseEurInput` rather than a local `parseFloat`: it is the one place in the application that
     * decides what "1.234,56" means, and a share is the same decimal problem as an amount.
     */
    const parsed = parseEurInput(next);
    if (parsed !== null) update(memberId, parsed);
  }

  /*
   * A REDISTRIBUTION REWRITES EVERY SHARE, so every draft is stale the moment one happens. Cleared
   * rather than reconciled: after `distributeEqually` the numbers are authoritative and any text
   * still on screen is what somebody typed before the row set changed.
   */
  function update(memberId: string, percentage: number): void {
    onChange(
      value.map((split) => (split.memberId === memberId ? { ...split, percentage } : split)),
    );
  }

  function remove(memberId: string): void {
    setDrafts({});
    // Redistributed on the way out too: removing one of three leaves 50/50 rather than 66.66% and a hole.
    onChange(distributeEqually(value.filter((split) => split.memberId !== memberId)));
  }

  function add(memberId: string): void {
    const member = members.find((entry) => entry.userId === memberId);
    if (!member) return;

    const added: EditableSplit = {
      memberId,
      memberDisplayName: member.displayName,
      percentage: 0,
      isFrozen: false,
    };

    setDrafts({});
    onChange(distributeEqually([...value, added]));
  }

  return (
    <fieldset className="rounded-control border border-line p-4">
      <legend className="px-1 text-sm font-semibold">{t("exp.split.heading")}</legend>
      <p className="text-sm text-muted">{t("exp.split.hint")}</p>

      {value.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-2">
          {value.map((split) => (
            <li key={split.memberId} className="flex items-center gap-3">
              <span className="flex min-w-32 items-center gap-2 text-sm">
                {/*
                  A frozen split still names a real person — `member_display_name` is snapshotted on
                  the row — so it keeps initials even though the lookup below misses. That is the
                  distinction between a departed MEMBER and a record that never knew who it was
                  about: this one still does.
                */}
                <Avatar
                  src={members.find((m) => m.userId === split.memberId)?.avatarUrl ?? null}
                  name={split.memberDisplayName}
                />
                {split.memberDisplayName}
                {split.isFrozen ? (
                  /*
                   * Stated in TEXT, not by styling alone. A greyed-out row does not tell a screen-reader user
                   * anything, and "no longer a member" is the whole explanation for why the field is locked.
                   */
                  <span className="ml-2 rounded-full border border-line px-2 py-0.5 text-xs text-muted">
                    {t("exp.split.former")}
                  </span>
                ) : null}
              </span>

              {/*
                NO `min`/`max`/`step`. They came with `type="number"` and only one of them ever did
                anything a reader could see — the spinner `step` — which is the affordance that made
                a scroll wheel change a share. The BOUND is the one this form actually enforces and
                it is not per-field: the shares must total exactly 100%, which the sentence below the
                list states and `splitsAreSubmittable` gates the submit on. A `max={100}` on a single
                row admits 100/100/100 and refuses a legitimate 100 alone, so it was never the rule.
              */}
              <DecimalInput
                value={textFor(split)}
                readOnly={split.isFrozen}
                aria-label={`${split.memberDisplayName}'s share, percent`}
                onValueChange={(next) => {
                  onShareTyped(split.memberId, next);
                }}
                className={
                  split.isFrozen ? "w-24 bg-panel text-muted" : "w-24 bg-input/30 text-ink"
                }
              />
              <span className="text-sm text-muted">%</span>

              {split.isFrozen ? null : (
                <button
                  type="button"
                  onClick={() => {
                    remove(split.memberId);
                  }}
                  className="text-sm text-muted underline decoration-dotted hover:text-ink"
                >
                  {t("common.remove")}
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : null}

      {available.length > 0 ? (
        <div className="mt-3 flex items-center gap-2">
          <label htmlFor="split-add" className="text-sm text-muted">
            {t("exp.split.addMember")}
          </label>
          <SelectField
            id="split-add"
            value=""
            onChange={(event) => {
              if (event.target.value !== "") add(event.target.value);
            }}
          >
            <option value="">{t("exp.split.choose")}</option>
            {available.map((member) => (
              <option key={member.userId} value={member.userId}>
                {member.displayName}
              </option>
            ))}
          </SelectField>
        </div>
      ) : null}

      {value.length > 0 ? (
        <p
          className={`mt-3 text-sm ${isExactlyHundred ? "text-muted" : "text-danger"}`}
          aria-live="polite"
        >
          {/* The CURRENT sum, always — a message that only states the rule is not actionable. */}
          Total {(totalHundredths / 100).toFixed(2)}%
          {isExactlyHundred ? "" : " — the shares must total exactly 100%"}
        </p>
      ) : null}
    </fieldset>
  );
}

/**
 * A committed share as the field shows it, in the reader's own number format.
 *
 * `formatPercent` is not used: it appends a `%`, and the sign sits beside the field as its own
 * element so a member never has to type or delete one. This is the same figure with the same
 * separator and nothing else.
 *
 * TRAILING ZEROES ARE DROPPED — 30, not 30.00. The stored share is exact to a hundredth and most of
 * them are whole numbers, so padding every field to two decimals would make the common case look
 * like a precision the household chose.
 */
function formatShareInput(percentage: number): string {
  return formatPercent(percentage).replace(/\s*%$/, "");
}

/** Whether a set of shares may be submitted: empty, or totalling exactly 100%. */
export function splitsAreSubmittable(splits: readonly EditableSplit[]): boolean {
  if (splits.length === 0) return true;
  return splits.reduce((sum, split) => sum + Math.round(split.percentage * 100), 0) === 10_000;
}

/**
 * An equal split across the editable shares, summing to EXACTLY 100%.
 *
 * ==========================================================================================
 * WHY PREFILLING AT ALL
 * ==========================================================================================
 *
 * Adding a member used to give them 0%, which meant the form opened refusing to submit and the member's
 * first job was arithmetic. The overwhelmingly common case is an even split — one member owns it, or two
 * share it, or three do — so that is what appears, and anyone who wants 60/40 types over it.
 *
 * ==========================================================================================
 * 33.33 × 3 IS 99.99, AND THAT IS THE WHOLE PROBLEM
 * ==========================================================================================
 *
 * The rule is exactly 100%, so an equal split of three cannot be three equal numbers. Largest remainder,
 * in integer HUNDREDTHS of a percent — the same units the total is checked in, because a float `33.33 +
 * 33.33 + 33.34` is `100.00000000000001` and needs an epsilon nobody can defend:
 *
 *     three members  ->  33.34 / 33.33 / 33.33
 *     six members    ->  16.67 / 16.67 / 16.67 / 16.67 / 16.66 / 16.66
 *
 * The extra hundredths go to the EARLIEST shares, which is arbitrary and has to be: they are equal by
 * definition, so there is no share with a better claim. What matters is that it is arbitrary the same way
 * every time, and that the sum is 100.00% before the member touches anything.
 *
 * ==========================================================================================
 * A FROZEN SHARE IS NOT REDISTRIBUTED
 * ==========================================================================================
 *
 * FR-033: a share belonging to someone who has left the household stays exactly as it is, and the equal
 * split covers what is left over. A split holding a frozen 40% therefore distributes 60% — two live members
 * get 30% each — and the total still comes to 100%. Redistributing the frozen share would be taking a
 * departed member's cost and quietly reassigning it.
 */
function distributeEqually(splits: readonly EditableSplit[]): EditableSplit[] {
  const editable = splits.filter((split) => !split.isFrozen);
  if (editable.length === 0) return [...splits];

  const frozenHundredths = splits
    .filter((split) => split.isFrozen)
    .reduce((sum, split) => sum + Math.round(split.percentage * 100), 0);

  // What the live members share. Never negative: a stored set summing above 100% cannot be produced by
  // this form or accepted by the server, and clamping keeps a corrupt one from producing a negative share.
  const available = Math.max(0, 10_000 - frozenHundredths);
  const base = Math.floor(available / editable.length);
  const remainder = available - base * editable.length;

  let index = 0;
  return splits.map((split) => {
    if (split.isFrozen) return split;
    const extra = index < remainder ? 1 : 0;
    index += 1;
    return { ...split, percentage: (base + extra) / 100 };
  });
}

/**
 * The same function, exported for its unit test.
 *
 * A named alias rather than exporting `distributeEqually` itself, so the module's public surface stays the
 * component: this is arithmetic with a rule the domain enforces, and it is worth testing directly rather
 * than through a rendered form where the assertion would be about input values.
 */
export { distributeEqually as distributeEquallyForTests };
